import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getCompanyIdForUser, findAccountIdByEmail } from '../services/firestoreService';
import {
  isQuickBooksConnected,
  getQuickBooksRevenueHistory
} from '../services/quickbooksService';
import {
  isJobTreadConnected,
  getJobTreadOrganizations,
  getJobTreadLeads,
  getJobTreadBacklog,
  getJobTreadProposals,
  getJobTreadCustomerOrders,
  computeCloseRatioFromLeads,
  resolveJobTreadOrg
} from '../services/jobtreadService';
import { resolveJobTreadFieldMappings } from '../services/jobtreadFieldMapping';
import { CLOSED_JOB_STATUSES, DEFAULT_PROPOSAL_PROBABILITY } from '../services/jobtreadConstants';
import { getMetricStatus, getCoachingCue } from '../components/sales/salesBenchmarks';
import { makeTrace, recordFetch, fmtN } from '../utils/metricTrace';

const EMPTY_METRIC = { value: '—', rawValue: null, status: 'none', source: 'none', series: [], sub: null, cue: null, trace: null };

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtCurrency = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`);
const fmtCompactCurrency = (v) =>
  v == null
    ? '—'
    : Math.abs(v) >= 1000000
    ? `$${(v / 1000000).toFixed(1)}M`
    : Math.abs(v) >= 1000
    ? `$${Math.round(v / 1000)}k`
    : `$${Math.round(v)}`;

/** ± band around the projected (not yet booked) portion of projections. */
const PROJECTION_RANGE = 0.15;
const fmtPercent = (v, digits = 1) =>
  v == null || Number.isNaN(v) ? '—' : `${(Math.round(v * Math.pow(10, digits)) / Math.pow(10, digits)).toFixed(digits)}%`;

const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Timeframe options for the analytics dashboard. 'ytd' preserves the classic behavior. */
export const SALES_TIMEFRAMES = [
  { id: 'ytd', label: 'This Year' },
  { id: 'last3', label: 'Last 3 Months' },
  { id: 'last6', label: 'Last 6 Months' },
  { id: 'last12', label: 'Last 12 Months' },
  { id: 'lastYear', label: 'Last Year' },
];

export const salesTimeframeLabel = (id) =>
  SALES_TIMEFRAMES.find((t) => t.id === id)?.label || 'This Year';

/**
 * Month buckets + date bounds for a timeframe. Months carry a 0-based
 * monthIndex; labels include a year suffix when the range crosses years.
 * Shared with useMarketingDashboard so both analytics tabs window identically.
 */
export const buildTimeframeRange = (timeframe, today) => {
  const y = today.getFullYear();
  const months = [];
  const push = (yy, mm) =>
    months.push({ year: yy, monthIndex: mm, label: `${MONTH_SHORT[mm]}${yy !== y ? ` '${String(yy).slice(2)}` : ''}` });
  if (timeframe === 'lastYear') {
    for (let m = 0; m < 12; m++) push(y - 1, m);
  } else if (timeframe === 'last3' || timeframe === 'last6' || timeframe === 'last12') {
    const n = timeframe === 'last3' ? 3 : timeframe === 'last6' ? 6 : 12;
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(y, today.getMonth() - i, 1);
      push(d.getFullYear(), d.getMonth());
    }
  } else {
    for (let m = 0; m < 12; m++) push(y, m);
  }
  const start = new Date(months[0].year, months[0].monthIndex, 1);
  const end = new Date(months[months.length - 1].year, months[months.length - 1].monthIndex + 1, 1);
  return { months, start, end };
};

/** Build monthly revenue series from manual revenueEntries for a given year. */
const manualRevenueSeries = (entries, year) => {
  const monthly = {};
  (entries || []).filter((e) => e.year === year).forEach((e) => {
    monthly[e.month] = (monthly[e.month] || 0) + num(e.amount);
  });
  return MONTH_FULL.map((m) => ({
    label: m.slice(0, 3),
    value: monthly[m] || 0
  }));
};

const manualRevenueYtd = (entries, year) =>
  (entries || []).filter((e) => e.year === year).reduce((sum, e) => sum + num(e.amount), 0);

/** Build monthly revenue series from QuickBooks revenueByMonth for current year. */
const qbRevenueSeries = (revenueByMonth, year) => {
  const map = new Map(
    (revenueByMonth || [])
      .filter((m) => m.year === year)
      .map((m) => [m.monthIndex, num(m.value)])
  );
  return MONTH_SHORT.map((label, i) => ({
    label,
    value: map.get(i + 1) || 0
  }));
};

const qbRevenueYtd = (revenueByMonth, year) =>
  (revenueByMonth || [])
    .filter((m) => m.year === year)
    .reduce((sum, m) => sum + num(m.value), 0);

/**
 * True when the year has at least one month whose income row actually parsed.
 * Legacy cache entries lack the `found` flag — treat those as parsed.
 */
const qbYearHasParsedData = (revenueByMonth, year) =>
  (revenueByMonth || []).some((m) => m.year === year && m.found !== false);

/** Compute close ratio from manual salesData.leads (exact status match). */
const manualCloseRatio = (leads) => {
  const list = leads || [];
  const total = list.length;
  const closed = list.filter((l) => CLOSED_JOB_STATUSES.includes((l.status || '').toLowerCase())).length;
  return {
    closeRatio: total > 0 ? Math.round((closed / total) * 1000) / 10 : 0,
    closedCount: closed,
    totalCount: total
  };
};

/** Count active partners by type using stage field (matches getPartnerStats). */
const countActivePartners = async (uid) => {
  const types = ['realtors', 'designers', 'architects'];
  const partnersRef = collection(db, 'partners');
  const counts = { realtors: 0, designers: 0, architects: 0, total: 0 };

  await Promise.all(
    types.map(async (type) => {
      try {
        const q = query(
          partnersRef,
          where('type', '==', type),
          where('stage', '==', 'Active'),
          where('createdBy', '==', uid)
        );
        const snap = await getDocs(q);
        counts[type] = snap.size;
        counts.total += snap.size;
      } catch (_) {
        // fallback: try status field for legacy data
        try {
          const q = query(
            partnersRef,
            where('type', '==', type),
            where('status', '==', 'Active'),
            where('createdBy', '==', uid)
          );
          const snap = await getDocs(q);
          counts[type] = snap.size;
          counts.total += snap.size;
        } catch (__) {
          /* ignore */
        }
      }
    })
  );

  return counts;
};

// userId/accountId let an internal viewer (e.g. CRM Portal View) load the
// dashboard for a specific client instead of the logged-in user.
export const useSalesDashboard = (timeframe = 'ytd', { jobtreadOnly = false, userId = null, accountId = null } = {}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reloadIndex, setReloadIndex] = useState(0);
  const refresh = () => setReloadIndex((i) => i + 1);
  const [state, setState] = useState({
    companyId: null,
    qbConnected: false,
    jobtreadConnected: false,
    jobtreadError: null,
    asOfDate: new Date(),
    debug: null,
    metrics: {
      revenue: EMPTY_METRIC,
      closeRatio: EMPTY_METRIC,
      pipeline: EMPTY_METRIC,
      partnerPrograms: EMPTY_METRIC,
      proposals: EMPTY_METRIC,
      awards: EMPTY_METRIC,
      designPricing: EMPTY_METRIC,
      salesScripts: EMPTY_METRIC,
      projectsSold: EMPTY_METRIC,
      avgJobSize: EMPTY_METRIC,
      nextQuarterProjection: EMPTY_METRIC,
      yearProjection: EMPTY_METRIC
    }
  });

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      const uid = userId || currentUser.uid;
      const today = new Date();
      const currentYear = today.getFullYear();
      const priorYear = currentYear - 1;
      const tf = buildTimeframeRange(timeframe, today);
      const tfLabel = salesTimeframeLabel(timeframe);
      const inTimeframe = (dateStr) => {
        const d = dateStr ? new Date(dateStr) : null;
        return !!d && !Number.isNaN(d.getTime()) && d >= tf.start && d < tf.end;
      };
      const monthPos = new Map(tf.months.map((mo, i) => [`${mo.year}-${mo.monthIndex}`, i]));
      const fetches = [];

      let companyId = accountId || null;
      if (!companyId) {
        try {
          companyId = await getCompanyIdForUser(uid);
          // Email fallback only applies when resolving the logged-in user —
          // currentUser.email is the wrong email for an overridden userId.
          if (!companyId && !userId && currentUser.email) {
            companyId = await findAccountIdByEmail(currentUser.email, 'Clients').catch(() => null);
          }
        } catch (_) {
          companyId = null;
        }
      }

      const qbOpts = companyId ? { accountId: companyId } : {};
      // JobTread grants live server-side; queries pass targetUserId and the
      // server resolves that user's grant (the caller's own when uid is theirs).
      const jobtreadConnected = await isJobTreadConnected(uid).catch(() => false);
      const qbConnected = await isQuickBooksConnected(companyId).catch(() => false);

      const [userDocRes, runwayDocRes, scriptsRes, partnerCounts] = await Promise.all([
        getDoc(doc(db, 'users', uid)),
        getDoc(doc(db, 'projectRunway', uid)),
        getDocs(collection(db, 'salesScripts')).catch(() => ({ size: 0, docs: [] })),
        countActivePartners(uid)
      ]);

      const userDoc = userDocRes.exists() ? userDocRes.data() : null;
      const revenueEntries = userDoc?.revenueEntries || [];
      const salesData = userDoc?.salesData || {};
      const designPricing = userDoc?.designPricing || null;
      const runwayDoc = runwayDocRes.exists() ? runwayDocRes.data() : null;

      let scriptsCount = 0;
      if (scriptsRes && typeof scriptsRes.size === 'number') {
        scriptsCount = scriptsRes.size;
      }

      /* -------- QuickBooks revenue (skipped in JobTread-only mode) -------- */
      let qbRevenueByMonth = [];
      let qbRevenueMeta = null;
      if (qbConnected && !jobtreadOnly) {
        try {
          const history = await getQuickBooksRevenueHistory({
            ...qbOpts,
            autoSyncIfEmpty: true,
            lookbackYears: 2
          });
          qbRevenueByMonth = Array.isArray(history?.revenueByMonth) ? history.revenueByMonth : [];
          qbRevenueMeta = history?.metadata || null;
          fetches.push({ key: 'qb.revenueHistory', fn: 'getQuickBooksRevenueHistory', params: { lookbackYears: 2, ...qbOpts }, status: 'fulfilled', error: null });
        } catch (e) {
          qbRevenueByMonth = [];
          fetches.push({ key: 'qb.revenueHistory', fn: 'getQuickBooksRevenueHistory', params: { lookbackYears: 2, ...qbOpts }, status: 'rejected', error: e?.message || String(e) });
        }
      }

      /* -------- JobTread -------- */
      let jtLeads = null;
      let jtLeadsDiagnostics = null;
      let backlog = null;
      let jtProposals = null;
      let jtProposalsDiagnostics = null;
      let jtOrders = null;
      let jtOrdersDiagnostics = null;
      let jtOrg = { orgId: null, orgName: null, orgCount: 0, multipleOrgs: false };
      let jtMapping = null;
      let jtError = null;
      if (jobtreadConnected) {
        // Every JobTread query runs under the target user's grant — passed
        // explicitly per call so overlapping runs can't poison each other.
        try {
          const orgsData = await getJobTreadOrganizations({ targetUserId: uid });
          jtOrg = resolveJobTreadOrg(orgsData);
          if (!jtOrg.orgId) {
            jtError = 'The JobTread grant key works but belongs to no organization.';
          }
          if (jtOrg.orgId) {
            const { mapping } = await resolveJobTreadFieldMappings(uid, jtOrg.orgId, { targetUserId: uid });
            jtMapping = mapping;
            const [leadsRes, backlogRes, proposalsRes, ordersRes] = await Promise.allSettled([
              getJobTreadLeads(jtOrg.orgId, { mapping, targetUserId: uid }),
              getJobTreadBacklog(jtOrg.orgId, { mapping, targetUserId: uid }),
              getJobTreadProposals(jtOrg.orgId, { mapping, targetUserId: uid }),
              jobtreadOnly ? getJobTreadCustomerOrders(jtOrg.orgId, { targetUserId: uid }) : Promise.resolve(null)
            ]);
            const leadsValue = recordFetch(fetches, 'jt.leads', 'getJobTreadLeads', { orgId: jtOrg.orgId }, leadsRes);
            if (leadsValue) {
              jtLeads = leadsValue.leads;
              jtLeadsDiagnostics = leadsValue.diagnostics;
            }
            backlog = recordFetch(fetches, 'jt.backlog', 'getJobTreadBacklog', { orgId: jtOrg.orgId }, backlogRes);
            const proposalsValue = recordFetch(fetches, 'jt.proposals', 'getJobTreadProposals', { orgId: jtOrg.orgId }, proposalsRes);
            if (proposalsValue) {
              jtProposals = proposalsValue.proposals;
              jtProposalsDiagnostics = proposalsValue.diagnostics;
            }
            const ordersValue = recordFetch(fetches, 'jt.customerOrders', 'getJobTreadCustomerOrders', { orgId: jtOrg.orgId }, ordersRes);
            if (ordersValue) {
              jtOrders = ordersValue.orders;
              jtOrdersDiagnostics = ordersValue.diagnostics;
            }
          }
        } catch (e) {
          jtError = e?.message || String(e);
          fetches.push({ key: 'jt.orgs', fn: 'getJobTreadOrganizations', params: null, status: 'rejected', error: jtError });
        }
        // Surface failures the per-fetch wrappers swallow into diagnostics —
        // otherwise a dead grant (e.g. expired JobTread subscription) renders
        // as an indistinguishable "no data" empty state.
        if (!jtError) {
          jtError =
            jtOrdersDiagnostics?.error ||
            jtLeadsDiagnostics?.error ||
            fetches.find((f) => f.key.startsWith('jt.') && f.status === 'rejected')?.error ||
            null;
        }
      }

      const metrics = {};
      metrics.projectsSold = EMPTY_METRIC;
      metrics.avgJobSize = EMPTY_METRIC;
      metrics.nextQuarterProjection = EMPTY_METRIC;
      metrics.yearProjection = EMPTY_METRIC;

      // REVENUE
      const qbYtd = qbRevenueYtd(qbRevenueByMonth, currentYear);
      const qbPriorYtd = qbRevenueYtd(qbRevenueByMonth, priorYear);
      const priorYearParsed = qbYearHasParsedData(qbRevenueByMonth, priorYear);
      const manualYtd = manualRevenueYtd(revenueEntries, currentYear);
      const manualPriorYtd = manualRevenueYtd(revenueEntries, priorYear);
      const qbParseDiag = qbRevenueMeta?.parseDiagnostics;
      const qbRevenueWarnings = qbParseDiag?.monthsMissingIncomeRow?.length
        ? [`P&L income row missing for months: ${qbParseDiag.monthsMissingIncomeRow.join(', ')} — those months read as $0.`]
        : [];

      if (jobtreadOnly) {
        // JobTread-only mode: revenue = approved customer-order prices in the
        // window (documents of type customerOrder), else manual entries.
        // QuickBooks never used. Projects sold and average job size come from
        // the same approved orders, grouped by job.
        const isApprovedOrder = (o) => o.status.includes('approved') || o.status.includes('signed');
        const isOpenOrder = (o) => /\b(pending|open|payable)\b/.test(o.status || '');
        const approvedOrders = (jtOrders || []).filter((o) => isApprovedOrder(o) && inTimeframe(o.createdAt));
        const pricedOrders = approvedOrders.filter((o) => o.price > 0);
        const series = tf.months.map(({ label }) => ({ label, value: 0, backlog: 0 }));
        let total = 0;
        pricedOrders.forEach((o) => {
          const d = new Date(o.createdAt);
          const i = monthPos.get(`${d.getFullYear()}-${d.getMonth()}`);
          if (i == null) return;
          series[i].value += o.price;
          total += o.price;
        });
        const orderWarnings = jtOrdersDiagnostics?.warnings || [];
        const zeroPricedApproved = approvedOrders.length - pricedOrders.length;

        // BACKLOG — pending/open/payable customer orders are potential revenue
        // that hasn't been won yet. Overlaid on the chart by created month.
        const openOrders = (jtOrders || []).filter((o) => isOpenOrder(o) && o.price > 0);
        const backlogValue = openOrders.reduce((sum, o) => sum + o.price, 0);
        openOrders.forEach((o) => {
          const d = o.createdAt ? new Date(o.createdAt) : null;
          if (!d || Number.isNaN(d.getTime())) return;
          const i = monthPos.get(`${d.getFullYear()}-${d.getMonth()}`);
          if (i == null) return;
          series[i].backlog += o.price;
        });
        const backlogInfo = openOrders.length > 0 ? { value: backlogValue, count: openOrders.length } : null;

        // Historical win rate across decided orders (won vs declined) — used
        // to estimate how much of the open backlog is likely to convert.
        let wonAll = 0;
        let declinedAll = 0;
        (jtOrders || []).forEach((o) => {
          const s = o.status || '';
          if (/\b(approved|paid|signed)\b/.test(s)) wonAll += 1;
          else if (/\b(declined|denied|rejected)\b/.test(s)) declinedAll += 1;
        });
        const winRate = wonAll + declinedAll > 0 ? wonAll / (wonAll + declinedAll) : null;
        const backlogCue = backlogInfo && winRate != null
          ? `${fmtCurrency(backlogValue * winRate)} of the ${fmtCurrency(backlogValue)} open backlog is likely to convert at your ${fmtPercent(winRate * 100, 0)} historical win rate.`
          : null;

        // PROJECTION PACE — average monthly revenue from this year's approved
        // orders (always current-year, independent of the selected timeframe).
        const approvedThisYear = (jtOrders || []).filter((o) => {
          if (!isApprovedOrder(o) || !(o.price > 0)) return false;
          const d = o.createdAt ? new Date(o.createdAt) : null;
          return !!d && !Number.isNaN(d.getTime()) && d.getFullYear() === currentYear && d <= today;
        });
        const ytdApprovedTotal = approvedThisYear.reduce((sum, o) => sum + o.price, 0);
        const daysInCurrentMonth = new Date(currentYear, today.getMonth() + 1, 0).getDate();
        const elapsedMonths = today.getMonth() + today.getDate() / daysInCurrentMonth;
        const monthlyPace = elapsedMonths >= 1 && ytdApprovedTotal > 0 ? ytdApprovedTotal / elapsedMonths : null;

        // MONTHLY TREND — least-squares fit over this year's completed months
        // (starting at the first month with revenue), so projections follow
        // the direction revenue is moving. Needs 3+ completed months; falls
        // back to the flat YTD pace otherwise.
        const curMonth = today.getMonth();
        const monthlyTotalsThisYear = Array.from({ length: 12 }, () => 0);
        approvedThisYear.forEach((o) => {
          monthlyTotalsThisYear[new Date(o.createdAt).getMonth()] += o.price;
        });
        const firstRevenueMonth = monthlyTotalsThisYear.findIndex((v, i) => v > 0 && i < curMonth);
        let projectMonth = null; // (monthIndex) => expected revenue for that month
        let trendBasis = null;
        if (firstRevenueMonth !== -1 && curMonth - firstRevenueMonth >= 3) {
          const xs = [];
          const ys = [];
          for (let i = firstRevenueMonth; i < curMonth; i += 1) {
            xs.push(i);
            ys.push(monthlyTotalsThisYear[i]);
          }
          const n = xs.length;
          const meanX = xs.reduce((s, x) => s + x, 0) / n;
          const meanY = ys.reduce((s, y) => s + y, 0) / n;
          // Monthly order totals are lumpy — one big month gives least-squares
          // a slope that explodes when extended 6+ months out. Theil–Sen
          // (median of pairwise slopes) ignores a single outlier month but
          // still tracks a genuine steady climb. Halve it for a conservative
          // extrapolation, and never project a month outside the range
          // they've actually produced this year.
          const pairSlopes = [];
          for (let a = 0; a < n; a += 1) {
            for (let b = a + 1; b < n; b += 1) {
              pairSlopes.push((ys[b] - ys[a]) / (xs[b] - xs[a]));
            }
          }
          const sorted = pairSlopes.sort((p, q) => p - q);
          const mid = Math.floor(sorted.length / 2);
          const rawSlope = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
          const slope = rawSlope / 2;
          const intercept = meanY - slope * meanX;
          const minMonth = Math.min(...ys);
          const maxMonth = Math.max(...ys);
          projectMonth = (i) => Math.min(maxMonth, Math.max(minMonth, intercept + slope * i));
          trendBasis = {
            type: 'trend',
            months: n,
            slope: Math.round(slope),
            rawSlope: Math.round(rawSlope),
            monthlyAvg: Math.round(meanY),
            clampRange: [Math.round(minMonth), Math.round(maxMonth)]
          };
        } else if (monthlyPace != null) {
          projectMonth = () => monthlyPace;
          trendBasis = { type: 'pace', monthlyPace: Math.round(monthlyPace) };
        }

        // On the YTD chart, stop the actual/backlog areas at the current month
        // and carry a dashed projection line through December.
        if (timeframe === 'ytd' && projectMonth) {
          const curIdx = monthPos.get(`${currentYear}-${curMonth}`);
          if (curIdx != null) {
            series.forEach((p, i) => {
              if (i > curIdx) {
                p.value = null;
                p.backlog = null;
                p.projected = Math.round(projectMonth(tf.months[i].monthIndex));
              } else if (i === curIdx) {
                p.projected = p.value;
              }
            });
          }
        }
        if (pricedOrders.length > 0) {
          metrics.revenue = {
            value: fmtCurrency(total),
            rawValue: total,
            status: 'none',
            source: 'jobtread',
            series,
            backlog: backlogInfo,
            sub: `${approvedOrders.length} approved customer order${approvedOrders.length === 1 ? '' : 's'} · ${tfLabel}`,
            cue: zeroPricedApproved > 0
              ? `${zeroPricedApproved} approved order${zeroPricedApproved === 1 ? ' has' : 's have'} no price in JobTread and add $0.`
              : null,
            trace: makeTrace('revenue', {
              source: 'jobtread',
              sourceReason: `JobTread-only mode: summing approved customer-order prices dated in ${tfLabel}; backlog = open/pending/payable order prices`,
              formula: `Revenue = Σ prices of ${approvedOrders.length} approved customer orders (${pricedOrders.length} priced, ${zeroPricedApproved} at $0) = ${fmtCurrency(total)}; backlog = Σ prices of ${openOrders.length} pending/open/payable orders = ${fmtCurrency(backlogValue)}`,
              inputs: { approvedInWindow: approvedOrders.length, pricedInWindow: pricedOrders.length, backlogOrders: openOrders.length, backlogValue, ordersFetched: jtOrders?.length || 0, statusCounts: jtOrdersDiagnostics?.statusCounts || null },
              warnings: orderWarnings
            })
          };
        } else if (revenueEntries.length) {
          const manualSeries = tf.months.map(({ year: yy, monthIndex, label }, i) => ({
            label,
            value: (revenueEntries || [])
              .filter((e) => e.year === yy && e.month === MONTH_FULL[monthIndex])
              .reduce((sum, e) => sum + num(e.amount), 0),
            backlog: series[i]?.backlog || 0
          }));
          const manualTotal = manualSeries.reduce((sum, p) => sum + p.value, 0);
          metrics.revenue = {
            value: fmtCurrency(manualTotal),
            rawValue: manualTotal,
            status: 'none',
            source: 'manual',
            series: manualSeries,
            backlog: backlogInfo,
            sub: `${tfLabel} · from your entries`,
            cue: 'Approve customer orders in JobTread for automatic revenue.',
            trace: makeTrace('revenue', {
              source: 'manual',
              sourceReason: 'JobTread-only mode: no approved customer orders in this window — using your revenue entries',
              formula: `Revenue = Σ entries in ${tfLabel} = ${fmtCurrency(manualTotal)}${backlogInfo ? `; backlog overlay = Σ prices of ${openOrders.length} pending/open/payable JobTread orders = ${fmtCurrency(backlogValue)}` : ''}`,
              inputs: { entryCount: revenueEntries.length, approvedInWindow: approvedOrders.length, backlogOrders: openOrders.length, backlogValue },
              warnings: orderWarnings
            })
          };
        } else if (backlogInfo) {
          // No won revenue yet, but there are open orders — show the backlog
          // so the chart still answers "what's in the pipeline?".
          metrics.revenue = {
            value: fmtCurrency(0),
            rawValue: 0,
            status: 'none',
            source: 'jobtread',
            series,
            backlog: backlogInfo,
            sub: `No approved customer orders in ${tfLabel} · ${openOrders.length} pending order${openOrders.length === 1 ? '' : 's'} open`,
            cue: backlogCue || 'Approve customer orders in JobTread to turn backlog into revenue.',
            trace: makeTrace('revenue', {
              source: 'jobtread',
              sourceReason: 'JobTread-only mode: no approved orders or manual entries in this window — showing open backlog only',
              formula: `Backlog = Σ prices of ${openOrders.length} pending/open/payable orders = ${fmtCurrency(backlogValue)}`,
              inputs: { backlogOrders: openOrders.length, backlogValue, statusCounts: jtOrdersDiagnostics?.statusCounts || null },
              warnings: orderWarnings
            })
          };
        } else {
          metrics.revenue = {
            ...EMPTY_METRIC,
            cue: 'Approve customer orders in JobTread or add monthly revenue entries.',
            trace: makeTrace('revenue', {
              source: 'none',
              sourceReason: 'JobTread-only mode: no approved customer orders and no manual entries',
              warnings: orderWarnings
            })
          };
        }

        // NEXT QUARTER + YEAR-END PROJECTIONS — month-by-month along the
        // trend line (or flat pace), with backlog conversion as the upside.
        if (projectMonth) {
          const nextQ = Math.floor(curMonth / 3) + 1;
          const nextQLabel = `Q${(nextQ % 4) + 1} ${currentYear + Math.floor(nextQ / 4)}`;
          const nextQStart = nextQ * 3; // can run past December — the trend extrapolates
          const nextQuarterValue = [0, 1, 2].reduce((sum, k) => sum + projectMonth(nextQStart + k), 0);
          // Rest of the current month = expected month minus what's already
          // booked (already counted in YTD) — never added on top of it.
          const bookedThisMonth = monthlyTotalsThisYear[curMonth];
          const remainingThisMonth = Math.max(0, projectMonth(curMonth) - bookedThisMonth);
          let remainderValue = remainingThisMonth;
          for (let i = curMonth + 1; i < 12; i += 1) remainderValue += projectMonth(i);
          const yearEndValue = ytdApprovedTotal + remainderValue;
          const usingTrend = trendBasis.type === 'trend';
          const basisLabel = usingTrend
            ? `monthly trend (${trendBasis.slope >= 0 ? '+' : '−'}${fmtCurrency(Math.abs(trendBasis.slope))}/mo)`
            : `${fmtCurrency(monthlyPace)}/mo pace`;
          const basisFormula = usingTrend
            ? `Trend = median pairwise (Theil–Sen) slope over ${trendBasis.months} completed months (avg ${fmtCurrency(trendBasis.monthlyAvg)}/mo; raw slope ${fmtCurrency(trendBasis.rawSlope)}/mo dampened to ${fmtCurrency(trendBasis.slope)}/mo; months clamped to ${fmtCurrency(trendBasis.clampRange[0])}–${fmtCurrency(trendBasis.clampRange[1])})`
            : `Pace = ${fmtCurrency(ytdApprovedTotal)} YTD ÷ ${elapsedMonths.toFixed(1)} elapsed months = ${fmtCurrency(monthlyPace)}/mo`;
          // Low–high band: ±PROJECTION_RANGE on the projected portion only —
          // revenue already booked (YTD) is certain and doesn't widen.
          const nextQuarterLow = nextQuarterValue * (1 - PROJECTION_RANGE);
          const nextQuarterHigh = nextQuarterValue * (1 + PROJECTION_RANGE);
          const yearEndLow = ytdApprovedTotal + remainderValue * (1 - PROJECTION_RANGE);
          const yearEndHigh = ytdApprovedTotal + remainderValue * (1 + PROJECTION_RANGE);
          const rangePct = `±${Math.round(PROJECTION_RANGE * 100)}%`;
          metrics.nextQuarterProjection = {
            ...EMPTY_METRIC,
            value: `${fmtCompactCurrency(nextQuarterLow)} – ${fmtCompactCurrency(nextQuarterHigh)}`,
            rawValue: nextQuarterValue,
            range: { low: nextQuarterLow, high: nextQuarterHigh },
            source: 'jobtread',
            sub: `${nextQLabel} · midpoint ${fmtCurrency(nextQuarterValue)} from your ${basisLabel}`,
            cue: usingTrend
              ? 'Follows the direction your monthly revenue is trending this year.'
              : 'Assumes your year-to-date pace holds.',
            trace: makeTrace('nextQuarterProjection', {
              source: 'jobtread',
              sourceReason: `${currentYear} approved customer orders, projected month-by-month over ${nextQLabel}`,
              formula: `${basisFormula}; ${nextQLabel} midpoint = Σ projected ${MONTH_SHORT[nextQStart % 12]}–${MONTH_SHORT[(nextQStart + 2) % 12]} = ${fmtCurrency(nextQuarterValue)}; range = midpoint ${rangePct} = ${fmtCurrency(nextQuarterLow)}–${fmtCurrency(nextQuarterHigh)}`,
              inputs: { ytdApprovedTotal, nextQuarterLow: Math.round(nextQuarterLow), nextQuarterHigh: Math.round(nextQuarterHigh), trendBasis, monthlyPace: monthlyPace != null ? Math.round(monthlyPace) : null, backlogValue, winRate },
              warnings: orderWarnings
            })
          };
          metrics.yearProjection = {
            ...EMPTY_METRIC,
            value: `${fmtCompactCurrency(yearEndLow)} – ${fmtCompactCurrency(yearEndHigh)}`,
            rawValue: yearEndValue,
            range: { low: yearEndLow, high: yearEndHigh },
            source: 'jobtread',
            sub: `Midpoint ${fmtCurrency(yearEndValue)} · ${fmtCurrency(ytdApprovedTotal)} YTD + ${fmtCurrency(remainderValue)} projected from your ${basisLabel}`,
            cue: backlogCue,
            trace: makeTrace('yearProjection', {
              source: 'jobtread',
              sourceReason: `${currentYear} approved-order revenue to date, extended along the ${usingTrend ? 'monthly trend line' : 'YTD pace'} through December`,
              formula: `${basisFormula}; Midpoint = ${fmtCurrency(ytdApprovedTotal)} YTD + ${fmtCurrency(remainderValue)} projected = ${fmtCurrency(yearEndValue)} (rest of ${MONTH_SHORT[curMonth]} = max(0, expected ${fmtCurrency(projectMonth(curMonth))} − ${fmtCurrency(bookedThisMonth)} already booked), then ${11 - curMonth} future months); range = YTD + projected ${rangePct} = ${fmtCurrency(yearEndLow)}–${fmtCurrency(yearEndHigh)}`,
              inputs: { ytdApprovedTotal, remainderValue: Math.round(remainderValue), yearEndLow: Math.round(yearEndLow), yearEndHigh: Math.round(yearEndHigh), bookedThisMonth: Math.round(bookedThisMonth), trendBasis, monthlyPace: monthlyPace != null ? Math.round(monthlyPace) : null, backlogValue, winRate },
              warnings: orderWarnings
            })
          };
        } else {
          const noPace = {
            source: 'none',
            sourceReason: 'No approved customer orders this year yet (or under one month of data) — nothing to project from',
            warnings: orderWarnings
          };
          metrics.nextQuarterProjection = { ...EMPTY_METRIC, cue: backlogCue, trace: makeTrace('nextQuarterProjection', noPace) };
          metrics.yearProjection = { ...EMPTY_METRIC, cue: backlogCue, trace: makeTrace('yearProjection', noPace) };
        }

        // PROJECTS SOLD + AVERAGE JOB SIZE — both use the SAME job set: distinct
        // jobs whose approved orders in the window carry a price. That makes
        // average job size exactly revenue ÷ projects sold (jobs with only
        // $0 approved orders are excluded from both, not just the average).
        const jobTotals = new Map();
        approvedOrders.forEach((o) => {
          const key = o.jobId || `order-${o.id}`;
          jobTotals.set(key, (jobTotals.get(key) || 0) + o.price);
        });
        const pricedTotals = [...jobTotals.values()].filter((v) => v > 0);
        const soldCount = pricedTotals.length;
        const zeroValueJobs = jobTotals.size - soldCount;
        const avgOrderJobSize = soldCount > 0
          ? pricedTotals.reduce((sum, v) => sum + v, 0) / soldCount
          : null;
        if (soldCount > 0) {
          metrics.projectsSold = {
            ...EMPTY_METRIC,
            value: soldCount.toLocaleString(),
            rawValue: soldCount,
            source: 'jobtread',
            sub: `Jobs with approved customer orders · ${tfLabel}`,
            trace: makeTrace('projectsSold', {
              source: 'jobtread',
              sourceReason: `Distinct jobs with priced approved customer orders in ${tfLabel}`,
              formula: `Projects sold = distinct jobs with approved order value > $0 = ${soldCount}${zeroValueJobs > 0 ? ` (${zeroValueJobs} jobs with only $0 approved orders excluded)` : ''}`,
              inputs: { approvedOrders: approvedOrders.length, soldCount, zeroValueJobs },
              warnings: orderWarnings
            })
          };
          metrics.avgJobSize = {
            ...EMPTY_METRIC,
            value: fmtCurrency(avgOrderJobSize),
            rawValue: avgOrderJobSize,
            source: 'jobtread',
            sub: `Revenue ÷ ${soldCount} project${soldCount === 1 ? '' : 's'} sold · ${tfLabel}`,
            trace: makeTrace('avgJobSize', {
              source: 'jobtread',
              sourceReason: `Approved customer-order totals grouped by job in ${tfLabel}`,
              formula: `Avg job size = revenue ${fmtCurrency(pricedTotals.reduce((sum, v) => sum + v, 0))} ÷ ${soldCount} projects sold = ${fmtCurrency(avgOrderJobSize)}`,
              inputs: { soldCount, zeroValueJobs },
              warnings: orderWarnings
            })
          };
        }
      } else if (qbConnected && qbRevenueByMonth.length > 0) {
        const series = qbRevenueSeries(qbRevenueByMonth, currentYear);
        // Only compute YoY when prior-year data actually parsed — otherwise a
        // cache of unparseable $0 months would fake a huge growth number.
        const yoyGrowth = qbPriorYtd > 0 && priorYearParsed ? ((qbYtd - qbPriorYtd) / qbPriorYtd) * 100 : null;
        const status = yoyGrowth != null ? getMetricStatus('revenueGrowth', yoyGrowth) : 'none';
        metrics.revenue = {
          value: fmtCurrency(qbYtd),
          rawValue: qbYtd,
          status,
          source: 'quickbooks',
          series,
          sub: yoyGrowth != null
            ? `${fmtPercent(yoyGrowth)} YoY · Prior year ${fmtCurrency(qbPriorYtd)}`
            : `${currentYear} YTD from QuickBooks`,
          cue: yoyGrowth != null ? getCoachingCue('revenueGrowth', status) : 'Revenue synced from QuickBooks.',
          trace: makeTrace('revenue', {
            source: 'quickbooks',
            sourceReason: `QuickBooks connected; cached revenue history has ${qbRevenueByMonth.length} month rows (last synced ${qbRevenueMeta?.lastSyncedAt || 'unknown'})`,
            formula: yoyGrowth != null
              ? `YTD = Σ monthly Total Income (${currentYear}) = ${fmtCurrency(qbYtd)}; YoY = (${fmtN(qbYtd)} − ${fmtN(qbPriorYtd)}) / ${fmtN(qbPriorYtd)} × 100 = ${fmtPercent(yoyGrowth)}`
              : `YTD = Σ monthly Total Income (${currentYear}) = ${fmtCurrency(qbYtd)} (no valid prior-year data for YoY)`,
            inputs: { ytd: qbYtd, priorYtd: qbPriorYtd, priorYearParsed, monthsCached: qbRevenueByMonth.length, lastSyncedAt: qbRevenueMeta?.lastSyncedAt || null, lastSyncSource: qbRevenueMeta?.lastSyncSource || null },
            warnings: qbRevenueWarnings
          })
        };
      } else if (revenueEntries.length) {
        const series = manualRevenueSeries(revenueEntries, currentYear);
        const yoyGrowth = manualPriorYtd > 0 ? ((manualYtd - manualPriorYtd) / manualPriorYtd) * 100 : null;
        const status = yoyGrowth != null ? getMetricStatus('revenueGrowth', yoyGrowth) : 'none';
        metrics.revenue = {
          value: fmtCurrency(manualYtd),
          rawValue: manualYtd,
          status,
          source: 'manual',
          series,
          sub: yoyGrowth != null
            ? `${fmtPercent(yoyGrowth)} YoY · ${currentYear} from your entries`
            : `${currentYear} YTD from your entries`,
          cue: 'Connect QuickBooks for automatic revenue tracking.',
          trace: makeTrace('revenue', {
            source: 'manual',
            sourceReason: qbConnected ? 'QuickBooks connected but revenue cache empty/failed — using your revenue entries' : 'QuickBooks not connected — using your revenue entries',
            formula: `YTD = Σ revenueEntries amounts (${currentYear}) = ${fmtCurrency(manualYtd)}${yoyGrowth != null ? `; YoY = (${fmtN(manualYtd)} − ${fmtN(manualPriorYtd)}) / ${fmtN(manualPriorYtd)} × 100 = ${fmtPercent(yoyGrowth)}` : ''}`,
            inputs: { entryCount: revenueEntries.length, ytd: manualYtd, priorYtd: manualPriorYtd },
            warnings: qbRevenueWarnings
          })
        };
      } else {
        metrics.revenue = { ...EMPTY_METRIC, cue: 'Connect QuickBooks or add monthly revenue entries.', trace: makeTrace('revenue', { source: 'none', sourceReason: 'No QuickBooks revenue cache and no manual entries', warnings: qbRevenueWarnings }) };
      }

      // Reframe revenue to the selected window ('ytd' keeps the classic YoY view;
      // the QuickBooks cache looks back 2 years, covering every other option).
      // JobTread-only mode already built its series over the window.
      if (!jobtreadOnly && timeframe !== 'ytd' && metrics.revenue.source !== 'none') {
        const useQb = metrics.revenue.source === 'quickbooks';
        const monthValue = ({ year: yy, monthIndex }) =>
          useQb
            ? num((qbRevenueByMonth.find((r) => r.year === yy && r.monthIndex === monthIndex + 1) || {}).value)
            : (revenueEntries || [])
                .filter((e) => e.year === yy && e.month === MONTH_FULL[monthIndex])
                .reduce((sum, e) => sum + num(e.amount), 0);
        const series = tf.months.map((mo) => ({ label: mo.label, value: monthValue(mo) }));
        const total = series.reduce((sum, p) => sum + p.value, 0);
        metrics.revenue = {
          ...metrics.revenue,
          value: fmtCurrency(total),
          rawValue: total,
          status: 'none',
          series,
          sub: `${tfLabel} · ${useQb ? 'from QuickBooks' : 'from your entries'}`,
          cue: null,
        };
      }

      // CLOSE RATIO
      // JobTread-only mode: bucket customer orders in the window by status —
      // won (approved/paid), open (pending/open/payable), declined. Falls back
      // to the leads-based computation when there are no orders in the window.
      const closeRatioOrders = jobtreadOnly ? (jtOrders || []).filter((o) => inTimeframe(o.createdAt)) : [];
      const orderBuckets = { won: 0, open: 0, declined: 0, other: 0 };
      const otherStatuses = {}; // e.g. { draft: 60, '(blank)': 4 } — named on the card
      closeRatioOrders.forEach((o) => {
        const s = o.status;
        if (/\b(approved|paid|signed)\b/.test(s)) orderBuckets.won += 1;
        else if (/\b(declined|denied|rejected)\b/.test(s)) orderBuckets.declined += 1;
        else if (/\b(pending|open|payable)\b/.test(s)) orderBuckets.open += 1;
        else {
          orderBuckets.other += 1;
          const key = s || '(blank)';
          otherStatuses[key] = (otherStatuses[key] || 0) + 1;
        }
      });
      const orderTotal = orderBuckets.won + orderBuckets.open + orderBuckets.declined;
      if (jobtreadOnly && orderTotal > 0) {
        const ratio = Math.round((orderBuckets.won / orderTotal) * 1000) / 10;
        const status = getMetricStatus('closeRatio', ratio);
        metrics.closeRatio = {
          value: fmtPercent(ratio),
          rawValue: ratio,
          status,
          source: 'jobtread',
          series: [],
          sub: `${orderBuckets.won} approved of ${orderTotal} customer orders · ${tfLabel}`,
          cue: null,
          closedCount: orderBuckets.won,
          openCount: orderBuckets.open,
          orderCounts: { ...orderBuckets, otherStatuses },
          trace: makeTrace('closeRatio', {
            source: 'jobtread',
            sourceReason: `Customer orders dated in ${tfLabel}, bucketed by status`,
            formula: `Close ratio = approved/paid ${orderBuckets.won} ÷ (won ${orderBuckets.won} + open ${orderBuckets.open} + declined ${orderBuckets.declined}) × 100 = ${fmtPercent(ratio)} (${orderBuckets.other} orders with other statuses excluded)`,
            inputs: { ...orderBuckets, statusCounts: jtOrdersDiagnostics?.statusCounts || null },
            warnings: jtOrdersDiagnostics?.warnings || []
          })
        };
      } else if (jobtreadOnly && jobtreadConnected) {
        // Documents-only: no leads fallback when JobTread is connected but the
        // window has no bucketable customer orders.
        metrics.closeRatio = {
          ...EMPTY_METRIC,
          trace: makeTrace('closeRatio', {
            source: 'none',
            sourceReason: 'No customer orders dated in this window',
            inputs: { statusCounts: jtOrdersDiagnostics?.statusCounts || null },
            warnings: jtOrdersDiagnostics?.warnings || []
          })
        };
      } else if (jtLeads?.length) {
        const scopedLeads = timeframe === 'ytd' ? jtLeads : jtLeads.filter((l) => inTimeframe(l.createdDate));
        const { closeRatio, closedCount, totalCount } = computeCloseRatioFromLeads(scopedLeads);
        const status = getMetricStatus('closeRatio', closeRatio);
        // Average job size across closed jobs that carry a contract value
        // (same closed predicate as computeCloseRatioFromLeads).
        const isClosedLead = (l) =>
          !!l.closedOn || CLOSED_JOB_STATUSES.some((s) => String(l.status || '').toLowerCase().includes(s));
        const closedJobValues = scopedLeads
          .filter(isClosedLead)
          .map((l) => num(l.estimatedProjectValue))
          .filter((v) => v > 0);
        const avgJobSize = closedJobValues.length
          ? closedJobValues.reduce((sum, v) => sum + v, 0) / closedJobValues.length
          : null;
        // Monthly closed vs open mix over the timeframe months, by created date.
        const monthlyLeadMix = tf.months.map(({ label }) => ({ label, closed: 0, open: 0 }));
        scopedLeads.forEach((l) => {
          const d = l.createdDate ? new Date(l.createdDate) : null;
          if (!d || Number.isNaN(d.getTime())) return;
          const i = monthPos.get(`${d.getFullYear()}-${d.getMonth()}`);
          if (i == null) return;
          if (isClosedLead(l)) monthlyLeadMix[i].closed += 1;
          else monthlyLeadMix[i].open += 1;
        });
        metrics.closeRatio = {
          value: fmtPercent(closeRatio),
          rawValue: closeRatio,
          status,
          source: 'jobtread',
          series: monthlyLeadMix.some((m) => m.closed + m.open > 0) ? monthlyLeadMix : [],
          sub: `${closedCount} closed of ${totalCount} jobs in JobTread${timeframe !== 'ytd' ? ` · ${tfLabel}` : ''}`,
          cue: getCoachingCue('closeRatio', status),
          closedCount,
          openCount: totalCount - closedCount,
          avgJobSize,
          avgJobSizeCount: closedJobValues.length,
          trace: makeTrace('closeRatio', {
            source: 'jobtread',
            sourceReason: `JobTread connected; ${totalCount} jobs fetched from org ${jtOrg.orgName || jtOrg.orgId}`,
            formula: `Close ratio = closed ${closedCount} / total ${totalCount} × 100 = ${fmtPercent(closeRatio)} (closed = closedOn set OR status contains one of: ${CLOSED_JOB_STATUSES.join(', ')})`,
            inputs: {
              closedCount,
              totalCount,
              jobsWithStatus: jtLeadsDiagnostics?.jobsWithStatus,
              jobsWithClosedOn: jtLeadsDiagnostics?.jobsWithClosedOn,
              orgId: jtOrg.orgId
            },
            warnings: jtLeadsDiagnostics?.warnings || []
          })
        };
      } else if (salesData.leads?.length) {
        const scopedLeads =
          timeframe === 'ytd'
            ? salesData.leads
            : salesData.leads.filter((l) => inTimeframe(l.createdAt || l.createdDate || l.date));
        const { closeRatio, closedCount, totalCount } = manualCloseRatio(scopedLeads);
        const status = getMetricStatus('closeRatio', closeRatio);
        const closedLeadValues = scopedLeads
          .filter((l) => CLOSED_JOB_STATUSES.includes((l.status || '').toLowerCase()))
          .map((l) => num(l.projectValue ?? l.estimatedProjectValue ?? l.value ?? l.amount))
          .filter((v) => v > 0);
        const avgJobSize = closedLeadValues.length
          ? closedLeadValues.reduce((sum, v) => sum + v, 0) / closedLeadValues.length
          : null;
        // Monthly closed vs open mix over the timeframe months, from dated leads.
        const monthlyLeadMix = tf.months.map(({ label }) => ({ label, closed: 0, open: 0 }));
        scopedLeads.forEach((l) => {
          const d = new Date(l.createdAt || l.createdDate || l.date || '');
          if (Number.isNaN(d.getTime())) return;
          const i = monthPos.get(`${d.getFullYear()}-${d.getMonth()}`);
          if (i == null) return;
          if (CLOSED_JOB_STATUSES.includes((l.status || '').toLowerCase())) monthlyLeadMix[i].closed += 1;
          else monthlyLeadMix[i].open += 1;
        });
        metrics.closeRatio = {
          value: fmtPercent(closeRatio),
          rawValue: closeRatio,
          status,
          source: 'manual',
          series: monthlyLeadMix.some((m) => m.closed + m.open > 0) ? monthlyLeadMix : [],
          sub: `${closedCount} closed of ${totalCount} leads tracked${timeframe !== 'ytd' ? ` · ${tfLabel}` : ''}`,
          cue: getCoachingCue('closeRatio', status),
          closedCount,
          openCount: totalCount - closedCount,
          avgJobSize,
          avgJobSizeCount: closedLeadValues.length,
          trace: makeTrace('closeRatio', {
            source: 'manual',
            sourceReason: jobtreadConnected ? 'JobTread returned no jobs — using your tracked leads' : 'JobTread not connected — using your tracked leads',
            formula: `Close ratio = closed ${closedCount} / total ${totalCount} × 100 = ${fmtPercent(closeRatio)} (exact status match)`,
            inputs: { closedCount, totalCount },
            warnings: jtLeadsDiagnostics?.warnings || []
          })
        };
      } else if (salesData.closeRatio != null) {
        const closeRatio = num(salesData.closeRatio);
        const status = getMetricStatus('closeRatio', closeRatio);
        metrics.closeRatio = {
          value: fmtPercent(closeRatio),
          rawValue: closeRatio,
          status,
          source: 'manual',
          series: [],
          sub: 'From your close ratio assessment',
          cue: getCoachingCue('closeRatio', status),
          trace: makeTrace('closeRatio', {
            source: 'manual',
            sourceReason: 'Using the single close-ratio value from your sales assessment',
            formula: `Close ratio = salesData.closeRatio = ${fmtPercent(closeRatio)} (entered directly)`,
            inputs: { closeRatio }
          })
        };
      } else {
        metrics.closeRatio = { ...EMPTY_METRIC, trace: makeTrace('closeRatio', { source: 'none', sourceReason: 'No JobTread jobs, tracked leads, or assessment value', warnings: jtLeadsDiagnostics?.warnings || [] }) };
      }

      // PIPELINE / PROJECT RUNWAY
      if (backlog && (backlog.weightedPipelineValue > 0 || backlog.backlogValue > 0 || backlog.activeJobCount > 0)) {
        const weighted = backlog.weightedPipelineValue || 0;
        const status = getMetricStatus('pipelineValue', weighted);
        metrics.pipeline = {
          value: fmtCurrency(weighted),
          rawValue: weighted,
          status,
          source: 'jobtread',
          series: [],
          sub: `${backlog.activeJobCount} active jobs · ${fmtCurrency(backlog.backlogValue)} signed backlog`,
          cue: `${backlog.proposalCount || 0} proposals in pipeline. ${getCoachingCue('pipelineValue', status)}`,
          trace: makeTrace('pipeline', {
            source: 'jobtread',
            sourceReason: `JobTread backlog computed from org ${jtOrg.orgName || jtOrg.orgId}`,
            formula: `Weighted pipeline = Σ proposalValue × probability = ${fmtCurrency(weighted)} (probability: ${backlog.diagnostics?.proposals?.probabilitySource || `fixed ${DEFAULT_PROPOSAL_PROBABILITY}`}); signed backlog = ${backlog.diagnostics?.backlogBasis || 'proposal totals'} = ${fmtCurrency(backlog.backlogValue)}`,
            inputs: {
              weightedPipelineValue: backlog.weightedPipelineValue,
              backlogValue: backlog.backlogValue,
              activeJobCount: backlog.activeJobCount,
              totalJobCount: backlog.totalJobCount,
              proposalCount: backlog.proposalCount,
              proposalsFromDocuments: backlog.diagnostics?.proposals?.fromDocuments,
              proposalsFromJobs: backlog.diagnostics?.proposals?.fromJobs
            },
            warnings: backlog.diagnostics?.warnings || []
          })
        };
      } else if (runwayDoc?.prospects?.length) {
        const prospects = runwayDoc.prospects;
        const weighted = prospects.reduce(
          (sum, p) => sum + num(p.projectedRevenue) * (num(p.likelyToSell) / 100),
          0
        );
        const status = getMetricStatus('pipelineValue', weighted);
        metrics.pipeline = {
          value: fmtCurrency(weighted),
          rawValue: weighted,
          status,
          source: 'manual',
          series: [],
          sub: `${prospects.length} prospects · weighted revenue`,
          cue: 'Connect JobTread for live pipeline from jobs and proposals.',
          trace: makeTrace('pipeline', {
            source: 'manual',
            sourceReason: jobtreadConnected ? 'JobTread backlog empty/unavailable — using your Project Runway prospects' : 'JobTread not connected — using your Project Runway prospects',
            formula: `Weighted = Σ projectedRevenue × likelyToSell% over ${prospects.length} prospects = ${fmtCurrency(weighted)}`,
            inputs: { prospectCount: prospects.length },
            warnings: backlog?.diagnostics?.warnings || []
          })
        };
      } else {
        metrics.pipeline = { ...EMPTY_METRIC, cue: 'Connect JobTread or add prospects on Project Runway.', trace: makeTrace('pipeline', { source: 'none', sourceReason: 'No JobTread backlog and no manual prospects' }) };
      }

      // PARTNER PROGRAMS
      if (partnerCounts.total > 0) {
        const status = getMetricStatus('partnerCount', partnerCounts.total);
        const breakdown = [
          partnerCounts.realtors > 0 ? `${partnerCounts.realtors} realtors` : null,
          partnerCounts.designers > 0 ? `${partnerCounts.designers} designers` : null,
          partnerCounts.architects > 0 ? `${partnerCounts.architects} architects` : null
        ].filter(Boolean).join(' · ');
        metrics.partnerPrograms = {
          value: partnerCounts.total.toLocaleString(),
          rawValue: partnerCounts.total,
          status,
          source: 'manual',
          series: [],
          sub: breakdown || 'Active referral partners',
          cue: getCoachingCue('partnerCount', status),
          trace: makeTrace('partnerPrograms', {
            source: 'manual',
            sourceReason: 'Counted from the partners collection (stage/status == Active, createdBy you)',
            formula: `Total = realtors ${partnerCounts.realtors} + designers ${partnerCounts.designers} + architects ${partnerCounts.architects} = ${partnerCounts.total}`,
            inputs: partnerCounts
          })
        };
      } else {
        metrics.partnerPrograms = { ...EMPTY_METRIC, cue: 'View the referral program overview and download the template.', trace: makeTrace('partnerPrograms', { source: 'none', sourceReason: 'No active partners found' }) };
      }

      // PROPOSALS
      if (jtProposals?.length) {
        const totalValue = jtProposals.reduce((sum, p) => sum + num(p.proposalValue), 0);
        metrics.proposals = {
          value: jtProposals.length.toLocaleString(),
          rawValue: jtProposals.length,
          status: 'none',
          source: 'jobtread',
          series: [],
          sub: `${fmtCurrency(totalValue)} total proposal value`,
          cue: 'Open proposals in JobTread or use our templates to close more deals.',
          trace: makeTrace('proposals', {
            source: 'jobtread',
            sourceReason: `JobTread documents typed proposal/estimate/quote/bid plus proposal-status jobs (org ${jtOrg.orgName || jtOrg.orgId})`,
            formula: `Count = ${jtProposals.length} (${jtProposalsDiagnostics?.fromDocuments ?? '—'} from documents, ${jtProposalsDiagnostics?.fromJobs ?? '—'} from jobs); total value = Σ proposalValue = ${fmtCurrency(totalValue)}`,
            inputs: { count: jtProposals.length, totalValue, fromDocuments: jtProposalsDiagnostics?.fromDocuments, fromJobs: jtProposalsDiagnostics?.fromJobs },
            warnings: jtProposalsDiagnostics?.warnings || []
          })
        };
      } else {
        metrics.proposals = { ...EMPTY_METRIC, cue: 'Connect JobTread to track open proposals, or use our templates.', trace: makeTrace('proposals', { source: 'none', sourceReason: jobtreadConnected ? 'JobTread returned no proposal documents or proposal-status jobs with value > 0' : 'JobTread not connected', warnings: jtProposalsDiagnostics?.warnings || [] }) };
      }

      // AWARDS
      const awardsList = salesData.awards || [];
      const awardsWon = awardsList.filter((a) => a.status === 'Won').length;
      const awardsCount = awardsWon > 0 ? awardsWon : num(salesData.awardCount);
      if (awardsCount > 0) {
        const status = getMetricStatus('awardsWon', awardsCount);
        metrics.awards = {
          value: awardsCount.toLocaleString(),
          rawValue: awardsCount,
          status,
          source: 'manual',
          series: [],
          sub: awardsList.length ? `${awardsList.length} tracked · ${awardsWon} won` : 'Awards won',
          cue: getCoachingCue('awardsWon', status),
          trace: makeTrace('awards', {
            source: 'manual',
            sourceReason: 'From salesData.awards entries (or awardCount when no list)',
            formula: awardsWon > 0 ? `Won = count(status == 'Won') = ${awardsWon} of ${awardsList.length} tracked` : `Count = salesData.awardCount = ${awardsCount}`,
            inputs: { tracked: awardsList.length, won: awardsWon, awardCount: salesData.awardCount ?? null }
          })
        };
      } else {
        metrics.awards = { ...EMPTY_METRIC, cue: 'Track industry awards to build credibility.', trace: makeTrace('awards', { source: 'none', sourceReason: 'No awards tracked' }) };
      }

      // DESIGN PRICING
      if (designPricing?.designFee > 0) {
        metrics.designPricing = {
          value: fmtCurrency(designPricing.designFee),
          rawValue: designPricing.designFee,
          status: 'none',
          source: 'manual',
          series: [],
          sub: designPricing.pricingTier
            ? `Tier: ${designPricing.pricingTier}`
            : designPricing.minPrice && designPricing.maxPrice
              ? `Range $${num(designPricing.minPrice).toLocaleString()}–$${num(designPricing.maxPrice).toLocaleString()}`
              : 'Design & feasibility fee',
          cue: 'Update your project range on the Design Pricing page.',
          trace: makeTrace('designPricing', {
            source: 'manual',
            sourceReason: 'From your Design Pricing calculator (users/{uid}.designPricing)',
            formula: `Design fee = ${fmtCurrency(designPricing.designFee)}`,
            inputs: { designFee: designPricing.designFee, pricingTier: designPricing.pricingTier ?? null }
          })
        };
      } else {
        metrics.designPricing = { ...EMPTY_METRIC, cue: 'Calculate your design service pricing.', trace: makeTrace('designPricing', { source: 'none', sourceReason: 'No design fee calculated' }) };
      }

      // SALES SCRIPTS
      const scriptCount = scriptsCount || num(salesData.salesScripts);
      if (scriptCount > 0) {
        metrics.salesScripts = {
          value: scriptCount.toLocaleString(),
          rawValue: scriptCount,
          status: 'none',
          source: 'manual',
          series: [],
          sub: scriptsCount > 0 ? 'Script meetings in your library' : 'Scripts tracked',
          cue: 'Master proven scripts to improve your sales conversations.',
          trace: makeTrace('salesScripts', {
            source: 'manual',
            sourceReason: scriptsCount > 0 ? 'Count of docs in the salesScripts collection' : 'From salesData.salesScripts',
            formula: `Count = ${scriptCount}`,
            inputs: { collectionCount: scriptsCount, manualCount: salesData.salesScripts ?? null }
          })
        };
      } else {
        metrics.salesScripts = { ...EMPTY_METRIC, cue: 'Add sales script meetings to your library.', trace: makeTrace('salesScripts', { source: 'none', sourceReason: 'No sales scripts found' }) };
      }

      const debug = {
        uid,
        companyId,
        fetches,
        connections: {
          quickbooks: {
            connected: qbConnected,
            accountId: companyId,
            lastRevenueSyncAt: qbRevenueMeta?.lastSyncedAt || null,
            lastSyncSource: qbRevenueMeta?.lastSyncSource || null,
            parseDiagnostics: qbRevenueMeta?.parseDiagnostics || null,
            integrationDocId: qbRevenueMeta?.quickbooksIntegrationDocId || null
          },
          jobtread: {
            connected: jobtreadConnected,
            orgId: jtOrg.orgId,
            orgName: jtOrg.orgName,
            orgCount: jtOrg.orgCount,
            multipleOrgs: jtOrg.multipleOrgs,
            fieldMappings: jtMapping,
            leadsDiagnostics: jtLeadsDiagnostics,
            proposalsDiagnostics: jtProposalsDiagnostics,
            backlogDiagnostics: backlog?.diagnostics || null
          }
        }
      };

      if (!cancelled) {
        setState({
          companyId,
          qbConnected,
          jobtreadConnected,
          jobtreadError: jtError,
          asOfDate: today,
          debug,
          metrics
        });
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser?.uid, currentUser?.email, reloadIndex, timeframe, jobtreadOnly, userId, accountId]);

  return { loading, refresh, ...state };
};

export default useSalesDashboard;
